import type { Territory } from '@amzn/vinyl-amzn'
import type { Maybe } from '@amazon/vinyl-util'
import { getLocale } from '@amzn/amp-i18n'

// Green Hornet's region groups (DomainConstants.ts); GH's `UK` market is the ISO code `GB`.
export const NA_TERRITORIES: ReadonlySet<Territory> = new Set<Territory>(['US', 'CA', 'MX', 'BR'])

export const ROW_NA_TERRITORIES: ReadonlySet<Territory> = new Set<Territory>([
    'AR',
    'CL',
    'CO',
    'BO',
    'CR',
    'DO',
    'EC',
    'SV',
    'GT',
    'HN',
    'NI',
    'PA',
    'PY',
    'PE',
    'UY',
])

export const EU_TERRITORIES: ReadonlySet<Territory> = new Set<Territory>([
    'GB',
    'DE',
    'FR',
    'IT',
    'ES',
    'IN',
])

export const ROE_TERRITORIES: ReadonlySet<Territory> = new Set<Territory>([
    'BE',
    'BG',
    'CY',
    'CZ',
    'EE',
    'FI',
    'GR',
    'HU',
    'IE',
    'IS',
    'LV',
    'LI',
    'LT',
    'LU',
    'MT',
    'NL',
    'PL',
    'PT',
    'SK',
    'SE',
])

export const FE_TERRITORIES: ReadonlySet<Territory> = new Set<Territory>(['JP', 'AU', 'NZ'])

export const CLASSIC_TERRITORIES: ReadonlySet<Territory> = new Set<Territory>([
    'US',
    'GB',
    'DE',
    'FR',
    'IT',
    'ES',
    'JP',
])

export const KNOWN_TERRITORIES: ReadonlySet<Territory> = new Set<Territory>([
    ...NA_TERRITORIES,
    ...ROW_NA_TERRITORIES,
    ...EU_TERRITORIES,
    ...ROE_TERRITORIES,
    ...FE_TERRITORIES,
])

export function isKnownTerritory(code: Maybe<string>): code is Territory {
    return code != null && (KNOWN_TERRITORIES as ReadonlySet<string>).has(code)
}

const TLD_TERRITORY = {
    com: 'US',
    ca: 'CA',
    'com.br': 'BR',
    'com.mx': 'MX',
    'co.uk': 'GB',
    de: 'DE',
    fr: 'FR',
    it: 'IT',
    es: 'ES',
    in: 'IN',
    'co.jp': 'JP',
    'com.au': 'AU',
} as const satisfies Record<string, Territory>

/** A territory with a retail site of its own. */
export type RetailTerritory = (typeof TLD_TERRITORY)[keyof typeof TLD_TERRITORY]

const TERRITORY_TLD = Object.fromEntries(
    Object.entries(TLD_TERRITORY).map(([tld, territory]) => [territory, tld])
) as Record<RetailTerritory, string>

const RETAIL_TERRITORY_OVERRIDES: Partial<Record<Territory, RetailTerritory>> = { NZ: 'AU' }

// A non-amazon host (localhost, *.amazon.dev) has no match and resolves to US.
const TLD_MATCH = new RegExp(
    `\\.amazon\\.(${Object.keys(TLD_TERRITORY)
        .map((tld) => tld.replaceAll('.', '\\.'))
        .join('|')})$`
)

export function territoryFromHostname(hostname: string): Territory {
    const tld = hostname.match(TLD_MATCH)?.[1] as keyof typeof TLD_TERRITORY | undefined
    return TLD_TERRITORY[tld ?? 'com']
}

/** The territory the page's amazon TLD serves; US off-web, where there is no `location`. */
export function hostTerritory(): Territory {
    return typeof location !== 'undefined' ? territoryFromHostname(location.hostname) : 'US'
}

function isRetailTerritory(territory: Territory): territory is RetailTerritory {
    return territory in TERRITORY_TLD
}

/**
 * The territory whose retail site serves `territory`: its own where it has one,
 * amazon.com.au for NZ, and amazon.com for every other territory.
 */
export function retailTerritory(territory: Territory): RetailTerritory {
    const retail = RETAIL_TERRITORY_OVERRIDES[territory] ?? territory
    return isRetailTerritory(retail) ? retail : 'US'
}

/** The territory's retail host (`www.amazon.co.uk` for GB), mirroring GH's getRetailDomain(). */
export function retailDomain(territory: Territory): string {
    return `www.amazon.${TERRITORY_TLD[retailTerritory(territory)]}`
}

/** A page on the territory's retail site, carrying the UI locale the way GH's cookie links do. */
export function retailUrl(territory: Territory, path: string): string {
    const url = new URL(path, `https://${retailDomain(territory)}`)
    url.searchParams.append('locale', getLocale().value.replace('-', '_'))
    return url.toString()
}

export function termsOfUseUrl(territory: Territory): string {
    return `https://${retailDomain(territory)}/gp/help/customer/display.html?pop-up=1&nodeId=201380010`
}

export function cookiePreferencesUrl(territory: Territory): string {
    return retailUrl(territory, '/privacyprefs/music')
}
